import { useMutation } from '@apollo/client';
import { S3Client } from '@aws-sdk/client-s3';
import { Upload as ManagedUpload } from '@aws-sdk/lib-storage';
import { LoadingButton } from '@mui/lab';
import { Button, LinearProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { PPT_MIME_TYPES } from '~/components/UploadDialog';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { CreateUploadConfigDocument } from '~/generated/graphql';
import { assert } from '~/lib/assert';
import type { MediaItemMenuProps__MediaItem } from './MediaItemMenu.generated';

export interface ReplaceMediaItemDialogProps {
  appCount: number;
  designCount: number;
  item: MediaItemMenuProps__MediaItem;
  onClose: () => void;
  onReplace: () => void;
  open: boolean;
}

export const ReplaceMediaItemDialog = ({
  appCount,
  designCount,
  item,
  onClose,
  onReplace,
  open,
}: ReplaceMediaItemDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [progress, setProgress] = useState<number | null>(null);
  const [createUploadConfig] = useMutation(CreateUploadConfigDocument);

  const resetState = useCallback(() => {
    onReplace();
    onClose();
    setProgress(() => null);
  }, [onClose, onReplace]);

  const { getInputProps, open: selectFile } = useDropzone({
    // Replace is same-kind only: the item's kind never changes.
    accept:
      item.kind === 'PDF'
        ? 'application/pdf'
        : item.kind === 'POWERPOINT'
        ? PPT_MIME_TYPES
        : `${item.kind}/*`,
    maxSize: 2 ** 30, // 1 GiB
    minSize: 1,
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      const file = accepted.at(0);
      if (!file) return;

      setProgress(() => 0);

      const { data } = await createUploadConfig({
        variables: {
          networkId: currentNetwork.id,
        },
      });
      assert(data?.createUploadConfig != null, 'Could not retrieve upload credentials');

      const { bucket, credentials, metadata, region } = data.createUploadConfig;
      const expiration = new Date(credentials.expiration);
      const client = new S3Client({ credentials: { ...credentials, expiration }, region });
      const manager = new ManagedUpload({
        client,
        params: {
          Body: file,
          Bucket: bucket,
          ContentType: file.type,
          Key: `uploads/media/${uuid()}`,
          Metadata: {
            ...metadata,
            filename: encodeURIComponent(file.name),
            mediaItemId: item.id.toString(),
          },
        },
      });
      manager.on('httpUploadProgress', (progress) => {
        if (progress.total) {
          setProgress((100 * (progress.loaded ?? 0)) / progress.total);
        }
      });
      manager.done().catch((error) => {
        console.error(error);
        resetState();
      });
    },
  });

  useEffect(() => {
    if (progress === 100) setTimeout(resetState, 1000);
  }, [progress, resetState]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle onClose={onClose}>
        {t('pages.content.media_details.replace_dialog.title')}
      </DialogTitle>
      <DialogContent>
        {progress !== null ? (
          <LinearProgress
            aria-label={t('pages.content.media_details.replace_dialog.title')}
            color="primary"
            value={progress}
            variant="determinate"
          />
        ) : (
          <DialogContentTitle>
            {t('pages.content.media_details.replace_dialog.message', {
              appCount,
              appPlural: appCount === 1 ? '' : 's',
              designCount,
              designPlural: designCount === 1 ? '' : 's',
            })}
          </DialogContentTitle>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <input {...getInputProps()} />
        <LoadingButton
          color="primary"
          loading={progress !== null}
          onClick={selectFile}
          variant="contained"
        >
          {t('pages.content.media_details.replace_dialog.replace')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
